import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Image from 'next/image'

import Settings from '@/src/assets/icons/Settings'
import SvgCameraDisabledIcon from '@/src/assets/icons/CameraDisabledIcon'
import SvgMicDisabledIcon from '@/src/assets/icons/MicDisabledIcon'
import SvgLock from '@/src/assets/icons/Lock'

const enum EBrowser {
  'Unknown' = 'Unknown',
  'Google Chrome' = 'Google Chrome',
  'Yandex' = 'Yandex',
  'Safari' = 'Safari',
  'Mozilla Firefox' = 'Mozilla Firefox',
  'Edge' = 'Edge',
  'Opera' = 'Opera',
}

const PopupInformation = ({ onClose }) => {
  const { t } = useTranslation()
  const [microphoneAccess, setMicrophoneAccess] = useState<boolean>(true)
  const [cameraAccess, setCameraAccess] = useState<boolean>(true)
  const [textPopup, setTextPopup] = useState<string>(t('cameraAndMicrophone'))
  const [isPermissionChecked, setIsPermissionChecked] = useState<boolean>(false)

  const isSafari = useMemo(() => /^((?!chrome|android).)*safari/i.test(navigator.userAgent), [])

  const browser = useMemo(() => {
    const userAgent = navigator.userAgent
    let browserName: EBrowser = EBrowser.Unknown

    if (userAgent.indexOf('Edge') > -1) {
      // old version Edge
      browserName = EBrowser.Edge
    } else if (userAgent.includes('Edg')) {
      // new version Edge
      return 'Edge'
    } else if (userAgent.indexOf('YaBrowser') > -1) {
      browserName = EBrowser.Yandex
    } else if (userAgent.indexOf('Opera') > -1) {
      browserName = EBrowser.Opera
    } else if (userAgent.indexOf('Firefox') > -1) {
      browserName = EBrowser['Mozilla Firefox']
    } else if (userAgent.indexOf('Chrome') > -1) {
      browserName = EBrowser['Google Chrome']
    } else if (userAgent.indexOf('Safari') > -1) {
      browserName = EBrowser.Safari
    }

    return browserName
  }, [])

  useEffect(() => {
    const checkPermissions = async () => {
      try {
        setIsPermissionChecked(true)

        const handlePermissionChange = () => {
          checkPermissions()
        }

        const cameraPermission = await navigator.permissions?.query({
          name: 'camera',
        } as unknown as PermissionDescriptor)
        const microphonePermission = await navigator.permissions.query({
          name: 'microphone',
        } as unknown as PermissionDescriptor)

        setCameraAccess(cameraPermission.state === 'granted')
        setMicrophoneAccess(microphonePermission.state === 'granted')

        cameraPermission.addEventListener('change', handlePermissionChange)
        microphonePermission.addEventListener('change', handlePermissionChange)

        return () => {
          cameraPermission.removeEventListener('change', handlePermissionChange)
          microphonePermission.removeEventListener('change', handlePermissionChange)
        }
      } catch (error) {
        console.error(t('permissionCheckError'), error)
      }
    }

    {
      /* 
     Строгая проверка на microphoneAccess === false нужна, так как до проверки пермишенов пользователя начальное состояние microphoneAccess это null и проверка !microphoneAccess в таком случае не подойдет. То же самое для cameraAccess */
    }
    if (microphoneAccess === false && cameraAccess === false) {
      setTextPopup(t('cameraAndMicrophoneBlocked'))
    }

    if (microphoneAccess && cameraAccess && isPermissionChecked) {
      onClose()
    }
    if (cameraAccess) {
      setTextPopup(t('microphoneBlocked'))
    }
    if (microphoneAccess) {
      setTextPopup(t('cameraBlocked'))
    }

    void checkPermissions()
  }, [microphoneAccess, cameraAccess, t])

  return (
    <div
      className="lk-toast-connection-state lk-toast lk-popup-content-access"
      style={{ zIndex: '99999' }}
    >
      <div className="lk-popup-content-access-box">
        <div>
          {isSafari && <img src="/images/video-camera-access-ios.png" alt="Proofix Meet" />}

          {browser === 'Google Chrome' && (
            <Image
              width={428}
              height={223}
              src="/images/video-camera-access-windows.png"
              alt="Proofix Meet"
            />
          )}

          {browser === 'Edge' && (
            <Image width={421} height={219} src="/images/edge.png" alt="Proofix Meet" />
          )}

          {browser === 'Yandex' && (
            <Image width={375} height={195} src="/images/yandex.png" alt="Proofix Meet" />
          )}

          {browser === 'Mozilla Firefox' && <img src="/images/mozila.png" alt="Proofix Meet" />}

          {browser === 'Opera' && (
            <Image width={398} height={223} src="/images/opera.png" alt="Proofix Meet" />
          )}
        </div>

        <div>
          <h2>
            {t('use')} {textPopup} {t('blocked')}
          </h2>

          {isSafari ? (
            <ul>
              <li>
                {t('safariMenuInstructions')} {window.location.hostname}...»
              </li>
              <li>{t('enableMicrophoneAndCamera')}</li>
              <li>{t('reload')}</li>
            </ul>
          ) : (
            <ul>
              <li>
                <span style={{ display: 'inline-block', margin: '0 5px 0 0' }}>
                  {t('clickIcon')}
                </span>

                {browser === 'Google Chrome' && (
                  <span>
                    {' '}
                    {t('settings')}
                    <Settings style={{ position: 'relative', top: '8px' }} />
                  </span>
                )}

                {isSafari && (
                  <span>
                    {' '}
                    {t('settings')}
                    <Settings style={{ position: 'relative', top: '8px' }} />
                  </span>
                )}

                {browser === 'Yandex' && (
                  <span>
                    <SvgCameraDisabledIcon style={{ position: 'relative', top: '4px' }} />
                    <span style={{ display: 'inline-block', margin: '0 5px' }}>{t('or')}</span>
                    <SvgMicDisabledIcon style={{ position: 'relative', top: '4px' }} />
                  </span>
                )}

                {browser === 'Mozilla Firefox' && (
                  <span>
                    <SvgCameraDisabledIcon style={{ position: 'relative', top: '4px' }} />
                    <span style={{ display: 'inline-block', margin: '0 5px' }}>{t('or')}</span>
                    <SvgMicDisabledIcon style={{ position: 'relative', top: '4px' }} />
                  </span>
                )}

                {browser === 'Edge' && (
                  <span>
                    <SvgLock style={{ position: 'relative', top: '4px' }} />
                  </span>
                )}

                {browser === 'Opera' && (
                  <span>
                    <SvgLock style={{ position: 'relative', top: '4px' }} />
                  </span>
                )}

                <br />

                {t('inAddressBar')}
              </li>
              <li>{t('enableMicrophoneAndCameraReload')}</li>
            </ul>
          )}

          {!isSafari && (
            <button className="lk-button" style={{ width: '100%' }} onClick={onClose}>
              {t('close')}
            </button>
          )}

          {isSafari && (
            <button
              className="lk-button"
              style={{ width: '100%' }}
              onClick={() => location.reload()}
            >
              {t('reload')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default PopupInformation
